import React, { useMemo } from 'react'
import { Box, Flex, Grid, Text } from '@chakra-ui/core'
import colors from 'constants/colors'
import LocaleText from 'components/LocaleText'
import { RankingCard } from './RankingCard'
import { IRankingReportObject } from 'constants/interfaces'
import { getUserAvatar } from 'hooks/useAvatar'
import { getUserFullName } from 'hooks/useUserName'

interface RankingSectionProps {
  title: string
  kpis: any[]
  kpisMediaData: any
  rankingReports: IRankingReportObject
  mappedRetailUsersByGroups: any
  retailUsersObject: any
  isDynamicKpi: boolean
  onKpiClick: (kpiId: string) => void
  headerAction?: React.ReactNode
}

const getStoreRank = (rankingReports: IRankingReportObject, kpiId: string) => {
  const { stores: currentRank } = rankingReports[kpiId] || {}
  return currentRank?.filter((rank: any) => !!rank.rank).sort((a: any, b: any) => a.rank - b.rank)?.[0]
}

const hasStoreProperties = (storeRank: any, isDynamicKpi: boolean): boolean => {
  return (
    (isDynamicKpi && storeRank && storeRank.diff !== null && storeRank.diff !== undefined) ||
    (storeRank && storeRank.target !== null && storeRank.target !== undefined) ||
    (storeRank && storeRank.performance !== null && storeRank.performance !== undefined)
  )
}

export const RankingSection: React.FC<RankingSectionProps> = ({
  title,
  kpis,
  kpisMediaData,
  rankingReports,
  mappedRetailUsersByGroups,
  retailUsersObject,
  isDynamicKpi,
  onKpiClick,
  headerAction,
}) => {
  const { kpisWithData, kpisWithoutData } = useMemo(() => {
    const withData: any[] = []
    const withoutData: any[] = []

    kpis.forEach((kpi) => {
      const storeRank = getStoreRank(rankingReports, kpi?.id)
      if (hasStoreProperties(storeRank, isDynamicKpi)) {
        withData.push(kpi)
      } else {
        withoutData.push(kpi)
      }
    })

    return { kpisWithData: withData, kpisWithoutData: withoutData }
  }, [kpis, rankingReports, isDynamicKpi])

  if (kpis.length === 0) return null

  const renderKpiCard = (kpi: any) => {
    const storeRank = getStoreRank(rankingReports, kpi?.id)
    const managersUid = mappedRetailUsersByGroups?.[storeRank?.group]?.[0]
    const kpiData = kpisMediaData[kpi.id]
    const kpiTitle = kpiData?.title
    const managersFullName = getUserFullName(managersUid, retailUsersObject ?? {})
    const managersAvatar = getUserAvatar(managersUid, retailUsersObject ?? {})

    return (
      <RankingCard
        kpiId={kpi.id}
        key={kpi.id}
        kpiTitle={kpiTitle}
        managersAvatar={managersAvatar}
        managersFullName={managersFullName}
        storeRank={storeRank}
        isDynamicKpi={isDynamicKpi}
        onClick={() => onKpiClick(kpi.id)}
      />
    )
  }

  return (
    <Box mb={['30px', '30px', '40px']}>
      <Flex alignItems="center" mb="20px" style={{ gap: '10px' }}>
        <Text fontSize={['12px', '14px', '16px']} color={colors.greyMain}>
          <LocaleText text={title} />
        </Text>
        {headerAction}
      </Flex>

      {kpisWithData.length > 0 && (
        <Grid
          templateColumns={[
            'repeat(auto-fill, minmax(min(100%, 350px), 1fr))',
            'repeat(auto-fill, minmax(min(100%, 350px), 1fr))',
            'repeat(2, 1fr)',
            'repeat(auto-fill, minmax(300px, 400px))',
          ]}
          gap={['16px', '32px', '40px']}
          w="100%"
          mb={kpisWithoutData.length > 0 ? ['16px', '32px', '40px'] : '0'}
        >
          {kpisWithData.map(renderKpiCard)}
        </Grid>
      )}

      {kpisWithoutData.length > 0 && (
        <Grid
          templateColumns={[
            'repeat(auto-fill, minmax(min(100%, 350px), 1fr))',
            'repeat(auto-fill, minmax(min(100%, 350px), 1fr))',
            'repeat(2, 1fr)',
            'repeat(auto-fill, minmax(300px, 400px))',
          ]}
          gap={['16px', '32px', '40px']}
          w="100%"
        >
          {kpisWithoutData.map(renderKpiCard)}
        </Grid>
      )}
    </Box>
  )
}
